import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Group } from 'app/tool/model/group.model';
import { ToolSet } from 'app/tool/model/toolSet.model';
import { TranslateService } from "@ngx-translate/core";
import { ToolStatus } from "@app/tool/model/enums/tool-status";
import { TableComponent } from '@components/custom-table/table.component';
import { Log } from "ng2-logger";
import { PartNumbersService } from "@services/part-numbers.service";
import { EquipmentService } from "@services/equipment.service";
import { LineService } from "@services/line.service";
import { ScannerDirective } from '@app/shared/directives/scanner.directive';
import { ToolAvailabilityStatus } from "@app/tool/model/enums/tool-availability-status";
import { map } from "rxjs/operators";
import { Tool } from "@app/tool/model/tool.model";
import { ToolService } from '@app/core/services/tool.service';
import { MenuItem } from 'primeng/api';

/**
 * TODO The logic for selecting items is disgusting.
 * The underlying table implementation should handle record selection, as to report selections made to parents.
 * We should refactor the table component
 */
@Component({
    selector: 'app-admin-tools-table',
    templateUrl: 'tools-table.component.html',
    styleUrls: ['tools-table.component.scss']
})
export class AdminToolsTableComponent implements OnInit {
    _logger = Log.create(AdminToolsTableComponent.name);

    @ViewChild(TableComponent, {static: true}) table: TableComponent;

    @Input() toolsGrouped = new Array<Group>();
    @Input() selectedTools = new Array<Tool | ToolSet>();
    @Input() tableConfig: TableConfig;
    @Input() treeMode = false;
    @Input() isLoading = false;
    @Input() showFilters = true;
    @Input() isFiltersExpanded = true;
    @Input() showGroupFilters = false;
    @Input() showGroupers = true;
    @Input() headerBackgroundColor: string;
    @Input() filterBySecLinesId: number;
    @Input() hasTitle = true;
    @Input() selectableTools = true;
    @Input() isReadOnly = false;
    @Input() canManageTools = false;
    @Input() isSelectAllChecked = false;
    @Input() hasSelectAll = false;

    @Output() pageChange = new EventEmitter<any>();
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterGroupChange = new EventEmitter<any>();
    @Output() groupChange = new EventEmitter<any>();
    @Output() sortChange = new EventEmitter<any>();
    @Output() openDetais = new EventEmitter<any>();
    @Output() openDetailsNewPage = new EventEmitter<any>();
    @Output() duplicateRow = new EventEmitter<any>();
    @Output() filterBySecLines = new EventEmitter<any>();
    @Output() filterByDocumentationValidity = new EventEmitter<any>();
    @Output() addToolIntoSet = new EventEmitter<any>();
    @Output() deleteSet = new EventEmitter<any>();
    @Output() onToolClicked = new EventEmitter<{ checked: boolean, group: Tool | ToolSet }>();
    @Output() selectAll =  new EventEmitter<any>();
    @Output() unselectAll = new EventEmitter<any>();

    isLoadingLines = false;
    isLoadingPartNumbers = false;
    isLoadingEquipments = false;

    onInit = true;

    filterAvailableForManufacturingList = [
        {name: 'All', value: 'All'},
        {name: 'Available', value: ToolAvailabilityStatus.AVAILABLE},
        {name: 'Not Available', value: ToolAvailabilityStatus.NOT_AVAILABLE_AUTOMATIC},
        {name: 'Not Available - Manual', value: ToolAvailabilityStatus.NOT_AVAILABLE_MANUAL}
    ];

    filterStatusList = [
        {name: 'All', value: 'All'},
        {name: 'Stored In Lift', value: ToolStatus.STORED_IN_LIFT},
        {name: 'At Atom Station', value: ToolStatus.AT_ATOM_STATION},
        {name: 'In Transport', value: ToolStatus.IN_TRANSPORT},
        {name: 'At Line', value: ToolStatus.AT_LINE},
        {name: 'At Facility', value: ToolStatus.AT_FACILITY}
    ];

    groupableColumns = [
        {id: 0, name: 'Part number', value: 'PartNumber'},
        {id: 1, name: 'Equipment', value: 'Equipment'},
        {id: 2, name: 'Line', value: 'Line'},
    ];

    groupFilters = [];

    filterDocumentTypesList = [];
    contextMenuItemsFactory = (event: any): MenuItem[] => this.buildContextMenuItems(event);

    // TODO datepicker component does not get translated dynamically.
    // This needs fixing
    constructor(private translateService: TranslateService,
                private partNumberService: PartNumbersService,
                private equipmentService: EquipmentService,
                private lineService: LineService,
                private toolService: ToolService,
                private cdr: ChangeDetectorRef) {
        this.filterAvailableForManufacturingList = this.buildAvailableForManufacturingFilterColumnKeys();
        this.filterStatusList = this.buildStatusFilterColumnKeys();
        this.groupFilters = this.buildGroupFilters();
        this.groupableColumns = this.buildGroupableColumnKeys();
        this.translateService.onLangChange.subscribe((event) => {
            this.filterAvailableForManufacturingList = this.buildAvailableForManufacturingFilterColumnKeys();
            this.filterStatusList = this.buildStatusFilterColumnKeys();
            this.groupFilters = this.buildGroupFilters();
            this.rebuildAllOptionForGroupFilter();
            this.groupableColumns = this.buildGroupableColumnKeys();
        });

    }

    ngOnInit() {
        this.filterGroupChange.emit(this.tableConfig);
        this.getLines();
        this.getEquipments();
        this.getPartNumbers();
        this.getToolDocumentTypes();
    }

    private addAllOptionForSelect(response, itemsName) {
        response[itemsName].unshift({
            id: '',
            name: this.translateService.instant('admin.tools.table.groupFilter.all.' + itemsName)
        });
        response.total++;
        return response;
    }

    private rebuildAllOptionForGroupFilter() {
        if (this.groupFilters.length > 0 && this.groupFilters[0].items.length > 0) {
            this.groupFilters[0].items[0].name = this.translateService.instant(
                'admin.tools.table.groupFilter.all.items');
        }
        if (this.groupFilters.length > 1 && this.groupFilters[1].items.length > 0) {
            this.groupFilters[1].items[0].name = this.translateService.instant(
                'admin.tools.table.groupFilter.all.equipment');
        }
        if (this.groupFilters.length > 2 && this.groupFilters[2].items.length > 0) {
            this.groupFilters[2].items[0].name = this.translateService.instant(
                'admin.tools.table.groupFilter.all.partNumbers');
        }
        if (this.groupFilters.length > 3 && this.groupFilters[3].items.length > 0) {
            this.groupFilters[3].items[0].name = this.translateService.instant(
                'admin.tools.table.groupFilter.all.documentTypes');
        }
    }

    private getLines() {
        this.isLoadingLines = true;
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;
        this.lineService.getAllLines(tableConfig).pipe(map(
            res => this.addAllOptionForSelect(res, 'items'))).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.items, 0);
                this.isLoadingLines = false;
            },
            error => {
                this._logger.error("getLines::", error);
                this.isLoadingLines = false;
            },
        )
    }

    private getEquipments() {
        this.isLoadingEquipments = true;
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;
        tableConfig.upsertFilter("onlyEquipment", true);
        this.equipmentService.getAllEquipment(tableConfig).pipe(map(
            res => this.addAllOptionForSelect(res, 'equipment'))).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.equipment, 1);
                this.isLoadingEquipments = false;
            },
            error => {
                this._logger.error("getEquipments::", error);
                this.isLoadingEquipments = false;
            });
    }

    private getPartNumbers() {
        this.isLoadingPartNumbers = true;
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;
        tableConfig.upsertFilter("onlySummary", true);
        this.partNumberService.getAllPartNumbers(tableConfig).pipe(map(
            res => this.addAllOptionForSelect(res, 'partNumbers')
        )).subscribe(
            response => {
                this.groupFilters = this.buildGroupFilters(response.partNumbers, 2);
                this.isLoadingPartNumbers = false;
            },
            error => {
                this._logger.error("getPartNumbers::", error);
                this.isLoadingPartNumbers = false;
            });
    }

    private getToolDocumentTypes() {
        this.toolService.getToolDocumentTypes().subscribe(
            response => {
            const wrapped = { documentTypes: response, total: response.length };
            const updated = this.addAllOptionForSelect(wrapped, 'documentTypes');
            this.filterDocumentTypesList = updated.documentTypes;
            this.groupFilters = this.buildGroupFilters(this.filterDocumentTypesList, 3);
            },
            error => {
            this._logger.error("getToolDocumentTypes::", error);
            }
        );
    }

    onToolUidChange(table: any, column: any, scannedValue: string) {
        if (ScannerDirective.UID_REGEX.test(scannedValue)) {
            this.cdr.detectChanges();
            const toolUid = scannedValue.replace(ScannerDirective.UID_REGEX, '$3');
            column.filterValue = toolUid;
            this._logger.info('Tool UID scanned', toolUid);
        }
        table.onFilterChange(column);
    }

    onPageChange(tableConfig: TableConfig) {
        this._logger.info('onPageChange::', tableConfig);
        this.pageChange.emit(tableConfig);
    }

    onSelectAll() {
        this.selectAll.emit();
    }

    onUnselectAll() {
        this.unselectAll.emit();
    }

    onSortChange(tableConfig: TableConfig) {
        this._logger.info('onSortChange::', tableConfig);
        this.sortChange.emit(tableConfig);
    }

    onFilterChange(tableConfig: TableConfig) {
        this._logger.info('onFilterChange::', tableConfig);
        this.filterChange.emit(tableConfig);
    }

    onFilterGroupChange(tableConfig: TableConfig) {
        if (this.onInit) {
            this.onInit = false;
            return;
        }
        for (const filter of this.tableConfig.filters) {
            if (filter.field === 'lineId') {
                this.groupFilters[0].selectedValue = filter.value;
            }
            if (filter.field === 'equipmentId') {
                this.groupFilters[1].selectedValue = filter.value;
            }
            if (filter.field === 'partNumberId') {
                this.groupFilters[2].selectedValue = filter.value;
            }
        }
        this._logger.info('onFilterGroupChange::', tableConfig);

        this.filterGroupChange.emit(this.tableConfig);
    }

    onFilterToggle(event) {
        if (event.type && event.type === 'first-filter') {
            this.filterBySecLines.emit(event.event);
        }

        if (event.type && event.type === 'second-filter') {
            this.filterByDocumentationValidity.emit(event.event);
        }
    }

    onDuplicate(duplicate) {
        this._logger.info('onDuplicate::', duplicate);
        this.duplicateRow.emit(duplicate);
    }

    onAddToolIntoSet(tool) {
        this._logger.info('onAddToolIntoSet::', tool);
        this.addToolIntoSet.emit(tool);
    }

    onDeleteSet(toolSet) {
        this._logger.info('onDeleteSet::', toolSet);
        this.deleteSet.emit(toolSet);
    }

    openToolDetails(tool) {
        this.openDetais.emit(tool);
    }

    onOpenToolDetailsInNewPage(tool) {
        this.openDetailsNewPage.emit(tool);
    }

    private buildContextMenuItems(event: any): MenuItem[] {
        if (event.record['isDeleted'] === true) {
            return [{label: this.translateService.instant('NotAvailable')}];
        }

        if (this.isToolSet(event.record)) {
            const toolSetActions: MenuItem[] = [];

            if (this.canManageTools) {
                toolSetActions.push({
                    label: this.translateService.instant('generic.buttons.add'),
                    icon: 'fa fa-plus-circle',
                    command: () => this.onAddToolIntoSet(event.record)
                });
            }

            if (this.canManageTools && event.record.isInStorage) {
                toolSetActions.push({
                    label: this.translateService.instant('generic.buttons.delete'),
                    icon: 'fa fa-trash',
                    command: () => this.onDeleteSet(event.record)
                });
            }

            return toolSetActions;
        }

        const toolActions: MenuItem[] = [];

        if (this.canManageTools) {
            toolActions.push({
                label: this.translateService.instant('generic.buttons.duplicate'),
                icon: 'fa fa-copy',
                command: () => this.onDuplicate(event.record)
            });
        }

        toolActions.push({
            label: this.translateService.instant('generic.buttons.openNewTab'),
            icon: 'fa fa-external-link',
            command: () => this.onOpenToolDetailsInNewPage(event.record)
        });

        return toolActions;
    }

    isGroup(record) {
        return record instanceof Group;
    }

    buildGroupableColumnKeys() {
        return [
            {
                id: 0,
                name: this.translateService.instant("admin.tools.table.grouping.options.line"),
                value: "Line"
            },
            {
                id: 1,
                name: this.translateService.instant("admin.tools.table.grouping.options.equipment"),
                value: "Equipment"
            },
            {
                id: 2,
                name: this.translateService.instant("admin.tools.table.grouping.options.partNumber"),
                value: "PartNumber"
            },
        ];
    }

    buildGroupFilters(items = [], id = -1) {
        return [
            {
                id: 0,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.line"),
                items: id === 0 && items.length > 0 ? items : ((this.groupFilters.length > 0 && this.groupFilters[0].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "lineId",
                selectedValue: (this.groupFilters.length > 0 && this.groupFilters[0].selectedValue) ?
                    this.groupFilters[0].selectedValue : '',
            },
            {
                id: 1,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.equipment"),
                items: id === 1 && items.length > 0 ? items : ((this.groupFilters.length > 1 && this.groupFilters[1].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "equipmentId",
                selectedValue: (this.groupFilters.length > 1 && this.groupFilters[1].selectedValue) ?
                    this.groupFilters[1].selectedValue : '',
            },
            {
                id: 2,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.partNumber"),
                items: id === 2 && items.length > 0 ? items : ((this.groupFilters.length > 2 && this.groupFilters[2].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "partNumberId",
                selectedValue: (this.groupFilters.length > 2 && this.groupFilters[2].selectedValue) ?
                    this.groupFilters[2].selectedValue : '',
            },
            {
                id: 3,
                placeholder: this.translateService.instant("admin.tools.table.grouping.options.requiredDocument"),
                items: id === 3 && items.length > 0 ? items : ((this.groupFilters.length > 3 && this.groupFilters[3].items) || []),
                displayField: 'name',
                value: "id",
                filterParam: "requiredDocumentType",
                selectedValue: (this.groupFilters.length > 3 && this.groupFilters[3].selectedValue) ?
                    this.groupFilters[3].selectedValue : '',
            }
        ];
    }

    buildAvailableForManufacturingFilterColumnKeys() {
        return [
            {name: this.translateService.instant("generic.statuses.all"), value: 'All'},
            {name: this.translateService.instant("generic.statuses.available"), value: ToolAvailabilityStatus.AVAILABLE},
            {name: this.translateService.instant("generic.statuses.notAvailable"), value: ToolAvailabilityStatus.NOT_AVAILABLE_AUTOMATIC},
            {
                name: this.translateService.instant("generic.statuses.notAvailableManual"),
                value: ToolAvailabilityStatus.NOT_AVAILABLE_MANUAL
            },
        ];
    }

    buildStatusFilterColumnKeys() {
        return [
            {name: this.translateService.instant("generic.statuses.all"), value: 'All'},
            {
                name: this.translateService.instant("generic.statuses.storedInLift"),
                value: ToolStatus.STORED_IN_LIFT
            },
            {
                name: this.translateService.instant("generic.statuses.atAtomStation"),
                value: ToolStatus.AT_ATOM_STATION
            },
            {
                name: this.translateService.instant("generic.statuses.inTransport"),
                value: ToolStatus.IN_TRANSPORT
            },
            {
                name: this.translateService.instant("generic.statuses.atLine"),
                value: ToolStatus.AT_LINE
            },
            {
                name: this.translateService.instant("generic.statuses.atFacility"),
                value: ToolStatus.AT_FACILITY
            }
        ];
    }

    // TODO this is copy paste logic for selected tools. We should have basic selection capabilities in the table component itself
    isToolSet(record) {
        return record instanceof ToolSet;
    }

    getSetForTool(tool: Tool): ToolSet | null {
        for (const el of this.toolsGrouped) {
            if (el instanceof ToolSet) {
                for (const toolOfSet of el.tools) {
                    if (toolOfSet.id === tool.id) {
                        return el;
                    }
                }
            }
        }
        return null;
    }

    isToolSelected(tool: Tool | ToolSet) {
        return this.selectedTools.filter(selectedTool => (
                (selectedTool instanceof Tool && tool instanceof Tool && selectedTool.id === tool.id) ||
                (selectedTool instanceof ToolSet && tool instanceof ToolSet && selectedTool.name === tool.name)
            )
        ).length > 0;
    };

    private handleSubtoolSelected(toolSet: ToolSet) {
        const selectedSubtools = toolSet.tools.filter(tool => this.isToolSelected(tool));
        for (const selectedSubTool of selectedSubtools) {
            this.onToolClicked.emit({checked: false, group: selectedSubTool})
        }
    }

    private handleToolChecked(selectedTool: Tool, setForSelectedTool: ToolSet) {
        if (setForSelectedTool) {
            let allChecked = true;
            for (const tool of setForSelectedTool.tools) {
                if (tool.id !== selectedTool.id && !this.isToolSelected(tool)) {
                    allChecked = false;
                }
            }
            if (allChecked) {
                for (const tool of setForSelectedTool.tools) {
                    if (this.isToolSelected(tool)) {
                        this.onToolClicked.emit({checked: false, group: tool});
                    }
                }
                this.onToolClicked.emit({checked: true, group: setForSelectedTool});
            } else {
                this.onToolClicked.emit({checked: true, group: selectedTool});
            }
        } else {
            this.onToolClicked.emit({checked: true, group: selectedTool});
        }
    }

    private handleToolUnchecked(unselectedTool, setForUnselectedTool) {
        if (setForUnselectedTool && this.isToolSelected(setForUnselectedTool)) {
            this.onToolClicked.emit({checked: false, group: setForUnselectedTool});
            for (const selectedTool of setForUnselectedTool.tools) {
                if (selectedTool.id !== unselectedTool.id) {
                    this.onToolClicked.emit({checked: true, group: selectedTool});
                }
            }
        } else {
            this.onToolClicked.emit({checked: false, group: unselectedTool});
        }
    }

    onClickTool(event: any, record: Tool | ToolSet) {
        event.stopPropagation();
        if (record instanceof ToolSet) {
            this.handleSubtoolSelected(record);
            this.onToolClicked.emit({checked: event.target.checked, group: record});
        } else if (record instanceof Tool) {
            const setForSelectedTool = this.getSetForTool(record);
            if (event.target.checked) {
                this.handleToolChecked(record, setForSelectedTool)
            } else {
                this.handleToolUnchecked(record, setForSelectedTool);
            }
        }
    }

    isSelectionDisabled(record) {
        return record.disabled;
    }

    hasCheckbox(record: any) {
        return !this.isReadOnly && (record instanceof Tool || record instanceof ToolSet);
    }

    toolSetContainsTool(tool: Tool, toolSet: ToolSet): boolean {
        return toolSet.tools.filter(toolFromToolSet => toolFromToolSet.id === tool.id).length > 0;
    }

    isChecked(record: Tool | ToolSet) {
        if (this.isToolSelected(record)) {
            return true;
        } else if (record instanceof Tool) {
            return this.selectedTools.filter(
                tool => tool instanceof ToolSet &&
                    this.toolSetContainsTool(record, tool)
            ).length > 0;
        }
    }
}
